import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { getFieldValue, RichText } from '@sitecore-jss/sitecore-jss-react';
import {
  useUiValue,
  setNationalNotificationStatus,
  setNationalNotificationHeight,
} from 'stores/uiContext';
import styled from 'styled-components';
import cx from 'classnames';
import { useMeasure } from 'react-use';
import ChevronDownIcon from 'components/icons/ChevronDown';
import { useCountryValue } from 'stores/country';
import { useSpring, animated } from 'react-spring';
import CloseIcon from 'components/icons/Close';
import breakpoints from 'utils/breakpoints';
import { PromotionBannerPopUp } from 'components/components/molecules';
import getPromotionPopupSessionKey from 'utils/promotionPopupSessionKey';
import { getMobileOS } from 'utils/getMobileOS';
import {
  hasSessionCookie,
  setSessionCookie,
  shouldSuppressPromotionPopupForSession,
} from 'utils/promotionPopupSessionCookie';

const BannerContainer = styled.div`
  min-height: 3.73rem;
  padding-left: 1.375rem;
  padding-right: 0.875rem;
  @media ${breakpoints.md} {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
  }
`;

const HIDE_NATIONAL_BANNER = 'hideNationalPromoBanner=';

const isEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const isIOSSafari = () => {
  if (typeof navigator === 'undefined') return false;

  return (
    getMobileOS() === 'iOS' &&
    /Safari/i.test(navigator.userAgent) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(navigator.userAgent)
  );
};

const PromotionBanner = ({ fields = {} }) => {
  // cookie check
  const hideNotificationSession = useMemo(() => {
    if (typeof window === 'undefined' || !document) return false;
    return (
      typeof document.cookie === 'string' &&
      document.cookie.includes(HIDE_NATIONAL_BANNER + 'true')
    );
  }, []);

  const [measureRef, { height }] = useMeasure();
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [{ country }] = useCountryValue();
  const promotionBanner = getFieldValue(fields, 'promotionBanner');
  const [promotionValues, setPromotionValues] = useState({});
  const [{ nationalNotificationActive, hasCommingSoonCommunity }, dispatch] =
    useUiValue();
  const [showCompleteBanner, setShowCompleteBanner] = useState(true);
  const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const enablePromotionPopup = isEnabled(promotionValues?.enablePopup);
  const shouldSyncVisualViewportTop = !isIOSSafari();

  const promotionPopupSessionKey = useMemo(() => {
    if (!promotionValues?.displayBanner || !promotionValues?.line2) return '';

    return getPromotionPopupSessionKey(promotionValues);
  }, [country, promotionValues]);

  // iOS Safari: when address bar collapses/expands, fixed top should follow visual viewport.
  const [vvTop, setVvTop] = useState(0);

  useEffect(() => {
    if (!shouldSyncVisualViewportTop) return undefined;

    const vv = window.visualViewport;
    if (!vv) return;

    let rafId = 0;

    const sync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const doc = document.documentElement;

        const maxScrollY = doc.scrollHeight - window.innerHeight;
        const y = window.scrollY;

        const atTop = y <= 0;
        const atBottom = y >= maxScrollY - 1;

        if (atTop || atBottom) {
          setVvTop(0);
          return;
        }

        const next = Math.max(0, vv.offsetTop || 0);
        setVvTop(next);
      });
    };

    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('scroll', sync, { passive: true });

    sync();

    return () => {
      cancelAnimationFrame(rafId);
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('scroll', sync);
    };
  }, [shouldSyncVisualViewportTop]);
  useEffect(() => {
    if (country === 'USA') {
      setPromotionValues(promotionBanner?.promotionBannerUSA || {});
    } else {
      setPromotionValues(promotionBanner?.promotionBannerCA || {});
    }
  }, [country, promotionBanner]);

  useEffect(() => {
    if (hasCommingSoonCommunity) {
      dispatch(setNationalNotificationStatus(false));
      return;
    }

    dispatch(
      setNationalNotificationStatus(
        !!promotionValues?.displayBanner && !hideNotificationSession,
      ),
    );
  }, [
    promotionValues,
    hasCommingSoonCommunity,
    hideNotificationSession,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !enablePromotionPopup ||
      !promotionPopupSessionKey ||
      hasCommingSoonCommunity
    ) {
      setShowPromotionPopup(false);
      return;
    }

    try {
      setShowPromotionPopup(
        !shouldSuppressPromotionPopupForSession() &&
          !hasSessionCookie(promotionPopupSessionKey),
      );
    } catch {
      setShowPromotionPopup(true);
    }
  }, [enablePromotionPopup, promotionPopupSessionKey, hasCommingSoonCommunity]);

  const textAlignment = 'center'; // right | left | center
  const [fixedBarVisible, setFixedBarVisible] = useState(false);

  const setBannerMeasureRef = useCallback(
    (node) => {
      bannerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const measureBannerHeight = useCallback(() => {
    if (!bannerRef.current) return;

    setBannerHeight(
      Math.ceil(bannerRef.current.getBoundingClientRect().height),
    );
  }, []);

  const handleOnScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const offset = 10;

    if (currentScrollY > offset) setFixedBarVisible(true);
    else setFixedBarVisible(false);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  useEffect(() => {
    if (
      nationalNotificationActive &&
      promotionValues &&
      promotionValues.line2
    ) {
      dispatch(setNationalNotificationHeight(bannerHeight || height));
    } else {
      dispatch(setNationalNotificationHeight(0));
    }
  }, [
    bannerHeight,
    height,
    nationalNotificationActive,
    promotionValues,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !nationalNotificationActive ||
      !(promotionValues && promotionValues.line2)
    ) {
      setBannerHeight(0);
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;

    const syncBannerHeight = () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);

      rafId = requestAnimationFrame(measureBannerHeight);
      timeoutId = window.setTimeout(measureBannerHeight, 250);
    };

    syncBannerHeight();
    window.addEventListener('resize', syncBannerHeight, { passive: true });
    window.addEventListener('orientationchange', syncBannerHeight);
    window.visualViewport?.addEventListener('resize', syncBannerHeight);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', syncBannerHeight);
      window.removeEventListener('orientationchange', syncBannerHeight);
      window.visualViewport?.removeEventListener('resize', syncBannerHeight);
    };
  }, [
    fixedBarVisible,
    height,
    measureBannerHeight,
    nationalNotificationActive,
    promotionValues,
    showCompleteBanner,
  ]);

  // check for links after rendering RichText Component
  useEffect(() => {
    const bannerContainer = document.getElementById(
      'national-notification-banner',
    );
    if (!bannerContainer) return;

    const links = bannerContainer.querySelectorAll('a');
    links.forEach((link) => {
      link.tabIndex = 0;
    });
  }, [promotionValues]);

  // Animate INNER container only (avoid transform on fixed wrapper)
  const innerStyle = useSpring({
    height: bannerHeight || (height ? height : 'auto'),
    config: { duration: shouldSyncVisualViewportTop ? 120 : 0 },
  });

  const closeNotification = () => {
    dispatch(setNationalNotificationStatus(false));
    document.cookie = HIDE_NATIONAL_BANNER + 'true; path=/;';
  };

  const closePromotionPopup = () => {
    if (promotionPopupSessionKey) {
      try {
        setSessionCookie(promotionPopupSessionKey);
      } catch {
        // Keep the popup closable if cookies are unavailable.
      }
    }

    setShowPromotionPopup(false);
  };

  const promotionPopup = (
    <PromotionBannerPopUp
      isOpen={showPromotionPopup}
      onClose={closePromotionPopup}
      promotionValues={promotionValues}
    />
  );

  if (
    !nationalNotificationActive ||
    !(promotionValues && promotionValues.line2)
  ) {
    return promotionPopup;
  }

  const portalRoot =
    typeof document !== 'undefined'
      ? document.getElementById('national-notification-banner')
      : null;

  if (!portalRoot) return promotionPopup;

  const banner = ReactDOM.createPortal(
    <div
      className={cx('bg-aqua z-40 md:z-50', {
        relative: !fixedBarVisible,
        'fixed left-0 right-0': fixedBarVisible,
      })}
      style={{
        top: fixedBarVisible ? vvTop : undefined,
        WebkitTransform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      <animated.div
        style={{
          ...innerStyle,
          overflow: 'hidden',
          willChange: 'height',
        }}
      >
        <BannerContainer
          ref={setBannerMeasureRef}
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
          className={cx('bg-aqua w-screen flex items-start justify-between', {
            absolute: !!height,
          })}
          role='region'
          aria-label='national notification'
        >
          <div
            className={cx('w-full py-4 flex flex-col', {
              'justify-center': textAlignment === 'center',
              'justify-start': textAlignment === 'left',
              'justify-end': textAlignment === 'right',
            })}
            onClick={(e) => {
              e.nativeEvent.stopPropagation();
            }}
          >
            {showCompleteBanner && promotionValues.line1 && (
              <span
                className={cx('text-promo-dark-blue font-graphie mr-4 w-full', {
                  'text-center': textAlignment === 'center',
                  'text-left': textAlignment === 'left',
                  'text-right': textAlignment === 'right',
                })}
              >
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: promotionValues.line1 }}
                />
              </span>
            )}

            <span
              className={cx(
                'text-promo-dark-blue font-trade-gothic-20 mr-4 w-full',
                {
                  'text-center': textAlignment === 'center',
                  'text-left': textAlignment === 'left',
                  'text-right': textAlignment === 'right',
                },
              )}
            >
              <div
                className={cx('flex flex-col md:flex-row', {
                  'items-center justify-center': textAlignment === 'center',
                  'items-left justify-start': textAlignment === 'left',
                  'items-right justify-end': textAlignment === 'right',
                })}
              >
                {promotionValues.marketPromotionLogo &&
                  promotionValues.marketPromotionLogo.src && (
                    <div className={cx(' mx-3', {})} aria-hidden='true'>
                      <div className='flex items-center justify-center mr-3 md:mt-0 h-16 mb-2 md:mb-0'>
                        <img
                          className='object-cover h-full'
                          src={promotionValues.marketPromotionLogo.src}
                          alt={promotionValues.marketPromotionLogo.alt}
                        />
                      </div>
                    </div>
                  )}
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: promotionValues.line2 }}
                />
              </div>
            </span>

            {showCompleteBanner && promotionValues.line3 && (
              <span
                className={cx('text-promo-dark-blue font-graphie mr-4 w-full', {
                  'text-center': textAlignment === 'center',
                  'text-left': textAlignment === 'left',
                  'text-right': textAlignment === 'right',
                })}
              >
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: promotionValues.line3 }}
                  onKeyDown={(event) => {
                    if (event.key === 'Tab' && !event.shiftKey) {
                      event.preventDefault();
                      const firstHeaderFocusableElement =
                        document.getElementById('logo-index');
                      if (firstHeaderFocusableElement)
                        firstHeaderFocusableElement.focus();
                    }
                  }}
                />
              </span>
            )}
          </div>

          <div className='flex flex-col-reverse h-full w-8'>
            <button
              id='national-notification-banner_index'
              className={cx('absolute bottom-0 pb-4', {
                hidden: !(promotionValues.line1 || promotionValues.line3),
              })}
              aria-label='Notification banner'
              aria-expanded={showCompleteBanner}
              onClick={(e) => {
                setShowCompleteBanner(!showCompleteBanner);
              }}
              onKeyDown={(event) => {
                if (
                  event.key === 'Tab' &&
                  !event.shiftKey &&
                  !promotionValues.line1 &&
                  !promotionValues.line2 &&
                  !promotionValues.line3
                ) {
                  event.preventDefault();
                  const firstHeaderFocusableElement =
                    document.getElementById('logo-index');
                  if (firstHeaderFocusableElement)
                    firstHeaderFocusableElement.focus();
                }
              }}
              onBlur={(e) => {
                e.preventDefault();
                const containers = [
                  document.getElementById('national-notification-banner'),
                  document.getElementById('market-notification-banner'),
                  document.getElementById('notification-banner'),
                ];

                containers.forEach((container) => {
                  if (!container) return;
                  const focusableElements =
                    container.querySelectorAll('a, button');
                  focusableElements.forEach((element) => {
                    element.setAttribute('tabindex', '0');
                  });
                });
              }}
            >
              <ChevronDownIcon
                className={cx('w-5 h-5 ml-1 mt-1 text-mattamy-blue transform', {
                  'rotate-180': showCompleteBanner,
                  'rotate-270': !showCompleteBanner,
                })}
              />
            </button>

            <button
              className='mt-4 hidden'
              onClick={closeNotification}
              aria-label='Close national promo banner'
            >
              <CloseIcon className='text-mattamy-blue w-6 h-6' />
            </button>
          </div>
        </BannerContainer>
      </animated.div>
    </div>,
    portalRoot,
  );

  return (
    <>
      {promotionPopup}
      {banner}
    </>
  );
};

export default PromotionBanner;
